// CÓMO SE LEE UNA FECHA EN PANTALLA. Un solo sitio.
//
// EL DEFECTO QUE CIERRA (QA, 23-sep-2026)
// ----------------------------------------
// En el MISMO formulario del Formato Amarillo convivían dos formatos de fecha:
//
//   · los campos EDITABLES son `<input type="date">`, y el navegador los pinta según la
//     configuración regional del usuario → «04/09/2026»;
//   · los de SOLO LECTURA salían con el valor crudo que manda la API → «2026-09-03».
//
// El operador lo vio en una captura: «Fecha de recepción: 2026-09-03» tres filas encima
// de «Fecha del informe inicial: 04/09/2026». Ninguno de los dos formatos era una
// decisión — eran dos valores por defecto que nadie eligió.
//
// POR QUÉ NO USA `Date` NI `Intl`
// --------------------------------
// Porque `new Date('2026-09-03')` se interpreta como MEDIANOCHE UTC, y en Lima (UTC-5)
// eso se muestra como el 2 de septiembre. En un sistema de farmacovigilancia una fecha
// de evento adverso corrida un día falsea un expediente que se presenta ante SENASA.
// Partir la cadena y recomponerla no tiene ese problema y no depende de la zona del
// navegador, del servidor ni del agente de integración continua. Es más aburrido y es
// más correcto.
//
// LO QUE NO HACE, Y ES LA MITAD DEL DISEÑO
// -----------------------------------------
// No adivina. Si lo que recibe no es exactamente `AAAA-MM-DD`, lo devuelve intacto. Eso
// cubre el caso peligroso: un campo de texto libre que casualmente contenga algo con
// forma de fecha no se reformatea, porque quien decide es la CLAVE del campo (la lista
// vive en la pantalla), no el aspecto del texto.
//
// Y sobre todo: esto es PRESENTACIÓN. Lo que viaja al servidor y el `value` de un
// `<input type="date">` siguen siendo ISO — la especificación de HTML lo exige y el
// backend lo espera. Formatear el dato en vez de la vista dejaría el control vacío.

/** `AAAA-MM-DD` y nada más: ni con hora, ni con zona, ni parcial. */
const SOLO_FECHA = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * La fecha como se lee en Perú: `2026-09-03` → `03/09/2026`.
 *
 * Cualquier otra cosa vuelve tal cual, en texto. `null` y `undefined` dan cadena vacía
 * para que quien la pinte decida su propio guión largo.
 */
export function comoFecha(valor: unknown): string {
  if (valor === null || valor === undefined) return '';
  const texto = String(valor);
  const partes = SOLO_FECHA.exec(texto);
  if (!partes) return texto;
  const [, anio, mes, dia] = partes;
  return `${dia}/${mes}/${anio}`;
}

/** ¿Es exactamente una fecha sin hora? Útil para decidir sin llamar al formateador. */
export const esFechaSola = (valor: unknown): boolean =>
  typeof valor === 'string' && SOLO_FECHA.test(valor);

/**
 * La zona en la que se lee un instante cuando el país del caso no tiene zona configurada, o la
 * que tiene no es válida. Es la MISMA que usa el servidor para contar los plazos
 * (`compartido/dominio/reloj.py`, `ZONA_HORARIA_POR_DEFECTO`): si la pantalla usara otra, el día
 * que muestra y el día con el que se cuenta el plazo podrían separarse.
 */
export const ZONA_HORARIA_POR_DEFECTO = 'America/Lima';

/** Un instante ISO con hora y CON desfase (`Z` o `±hh:mm`): el único que se puede ubicar. */
const INSTANTE_CON_ZONA = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})$/i;

/** Los formateadores por zona, uno por zona y no uno por llamada: una bandeja pinta cientos de fechas. */
const formateadores = new Map<string, Intl.DateTimeFormat>();

/** El formateador AAAA-MM-DD de la zona pedida; con una zona vacía o que no existe, el de Lima. */
function formateadorDe(zonaHoraria: string | null | undefined): Intl.DateTimeFormat {
  const zona = zonaHoraria || ZONA_HORARIA_POR_DEFECTO;
  const enCache = formateadores.get(zona);
  if (enCache) return enCache;
  let formateador: Intl.DateTimeFormat;
  try {
    // `en-CA` escribe AAAA-MM-DD; las partes se leen por tipo, así que el orden da igual.
    formateador = new Intl.DateTimeFormat('en-CA', { timeZone: zona, year: 'numeric', month: '2-digit', day: '2-digit' });
  } catch {
    // RangeError: la zona no es IANA. No se inventa otra: se usa el mismo respaldo del servidor.
    return formateadorDe(ZONA_HORARIA_POR_DEFECTO);
  }
  formateadores.set(zona, formateador);
  return formateador;
}

/** El día `AAAA-MM-DD` de un instante visto desde la zona dada. */
function diaEnZona(instante: Date, zonaHoraria: string | null | undefined): string {
  const partes = formateadorDe(zonaHoraria).formatToParts(instante);
  const parte = (tipo: Intl.DateTimeFormatPartTypes) => partes.find((p) => p.type === tipo)?.value ?? '';
  return `${parte('year')}-${parte('month')}-${parte('day')}`;
}

/** Los relojes por zona (fecha y hora de pared, 24 h), uno por zona como los de arriba. */
const relojes = new Map<string, Intl.DateTimeFormat>();

/** El reloj de pared de la zona pedida; con una zona vacía o que no existe, el de Lima. */
function relojDe(zonaHoraria: string | null | undefined): Intl.DateTimeFormat {
  const zona = zonaHoraria || ZONA_HORARIA_POR_DEFECTO;
  const enCache = relojes.get(zona);
  if (enCache) return enCache;
  let reloj: Intl.DateTimeFormat;
  try {
    reloj = new Intl.DateTimeFormat('en-US', {
      timeZone: zona, hourCycle: 'h23',
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  } catch {
    return relojDe(ZONA_HORARIA_POR_DEFECTO);
  }
  relojes.set(zona, reloj);
  return reloj;
}

/** Las partes numéricas de la fecha y hora de pared de un instante en la zona dada. */
function paredEnZona(instante: Date, zonaHoraria: string | null | undefined) {
  const partes = relojDe(zonaHoraria).formatToParts(instante);
  const numero = (tipo: Intl.DateTimeFormatPartTypes) => Number(partes.find((p) => p.type === tipo)?.value ?? 0);
  return {
    anio: numero('year'), mes: numero('month'), dia: numero('day'),
    hora: numero('hour'), minuto: numero('minute'), segundo: numero('second'),
  };
}

/** Cuánto adelanta (o atrasa) la zona respecto de UTC en ese instante, en milisegundos. */
function desfaseEn(instanteMs: number, zonaHoraria: string | null | undefined): number {
  const p = paredEnZona(new Date(instanteMs), zonaHoraria);
  const paredComoUtc = Date.UTC(p.anio, p.mes - 1, p.dia, p.hora, p.minuto, p.segundo);
  return paredComoUtc - Math.floor(instanteMs / 1000) * 1000;
}

/**
 * El PRIMER INSTANTE de un día (`AAAA-MM-DD`) en la zona del país del caso, en ISO UTC (RN-82):
 * `2026-10-06` en `America/Mexico_City` → `2026-10-06T06:00:00.000Z`.
 *
 * EL DEFECTO QUE CIERRA (Bug #1086, 6-oct-2026)
 * ----------------------------------------------
 * La fecha de recepción (y la de inicio del evento) se elige como día y el servidor la guarda
 * como instante. Se construía la medianoche del NAVEGADOR: un caso de México registrado desde
 * Lima quedaba a las 05:00 UTC, las 23:00 del día anterior en México, y el servidor contaba el
 * plazo desde ese día anterior (`dia_en_zona`). La medianoche tiene que ser la del país del caso,
 * la misma zona en la que el servidor lee el día.
 *
 * El día en que la zona adelanta el reloj justo a medianoche (Chile, primer domingo de septiembre)
 * las 00:00 no existen: se devuelve el primer instante que sí existe de ese día (las 01:00), que
 * es lo que hace también el servidor. Sin zona, o con una que no existe, la de Lima.
 *
 * Lo que no es `AAAA-MM-DD` devuelve `undefined`: aquí no se adivina.
 */
export function instanteDeMedianoche(fecha: unknown, zonaHoraria: string | null | undefined): string | undefined {
  const partes = SOLO_FECHA.exec(String(fecha ?? ''));
  if (!partes) return undefined;
  const [, anio, mes, dia] = partes;
  const base = Date.UTC(Number(anio), Number(mes) - 1, Number(dia));
  if (Number.isNaN(base)) return undefined;
  // Dos pasadas: el desfase se mide primero en la medianoche UTC y luego en el candidato, por si
  // entre los dos la zona cambió de horario. De los candidatos vale el más temprano que cae en el día.
  const primero = base - desfaseEn(base, zonaHoraria);
  const segundo = base - desfaseEn(primero, zonaHoraria);
  const delDia = [primero, segundo]
    .filter((t) => diaEnZona(new Date(t), zonaHoraria) === String(fecha))
    .sort((a, b) => a - b);
  return new Date(delDia[0] ?? primero).toISOString();
}

/**
 * La FECHA de un valor que puede traer hora, tomada en la ZONA DEL PAÍS DEL CASO (RN-82):
 * `2026-10-07T03:30:00Z` en `America/Lima` → `06/10/2026`.
 *
 * EL DEFECTO QUE CIERRA (Bug #1078, 6-oct-2026)
 * ----------------------------------------------
 * Antes se quedaba con los diez primeros caracteres del ISO. El servidor manda los instantes en
 * UTC, así que eso era el día de Greenwich: desde las 19:00 de Lima la pantalla mostraba el día
 * SIGUIENTE (un caso cerrado a las 21:00 del 6 decía «cerrado por … · 07/10/2026»). Lo cazó la
 * E2E del cierre de un caso grave al correrla de noche.
 *
 * POR QUÉ LA ZONA ES OBLIGATORIA Y ES LA DEL PAÍS
 * ------------------------------------------------
 * Decisión del operador: la fecha de un instante es la del país del caso, la misma zona en la
 * que se cuentan sus plazos (RN-20). Un caso de Chile muestra la fecha de Chile aunque lo mire
 * alguien en Lima. El parámetro no tiene valor por defecto a propósito: cada pantalla tiene que
 * decir de qué país es el dato (`useZonaDelPais`), y olvidarlo no compila. `null` sí es un
 * valor válido (el país no tiene zona, o el dato no es de ningún país) y se lee en Lima.
 *
 * LO QUE NO CAMBIA
 * ----------------
 * Una fecha SIN hora (`2026-09-24`) no se convierte: no tiene hora que mover, y construir un
 * `Date` con ella la pondría a medianoche UTC. Y lo que no es ni fecha ni instante con desfase
 * sale como salía antes, recortado a diez caracteres: el cambio es de zona, no de qué se muestra.
 */
export function comoFechaDeInstante(valor: unknown, zonaHoraria: string | null | undefined): string {
  if (valor === null || valor === undefined) return '';
  return comoFecha(fechaIsoDeInstante(valor, zonaHoraria));
}

/**
 * Lo mismo que `comoFechaDeInstante`, pero el día queda en ISO (`AAAA-MM-DD`): para guardarlo en
 * un campo de fecha o en una lista que después pasa por `comoFecha`. Misma regla (RN-82).
 */
export function fechaIsoDeInstante(valor: unknown, zonaHoraria: string | null | undefined): string {
  if (valor === null || valor === undefined) return '';
  const texto = String(valor);
  if (INSTANTE_CON_ZONA.test(texto)) {
    const instante = new Date(texto);
    if (!Number.isNaN(instante.getTime())) return diaEnZona(instante, zonaHoraria);
  }
  return texto.slice(0, 10);
}

/**
 * El día de HOY en la zona del país, en ISO (`AAAA-MM-DD`), para el valor inicial de un campo de
 * fecha. `new Date().toISOString().slice(0, 10)` es el día UTC: desde las 19:00 de Lima ya es
 * mañana, y el campo nacía con una fecha que todavía no llegó (RN-82).
 */
export function fechaDeHoyEnZona(zonaHoraria: string | null | undefined, ahora: Date = new Date()): string {
  return diaEnZona(ahora, zonaHoraria);
}

/**
 * La zona IANA que de verdad se usa para `zonaHoraria`: la misma, o `America/Lima` si viene vacía o
 * no existe (el respaldo del servidor). Sale del formateador ya validado, así que no repite la regla.
 */
function zonaQueSeUsa(zonaHoraria: string | null | undefined): string {
  return formateadorDe(zonaHoraria).resolvedOptions().timeZone;
}

/**
 * Un instante con su hora, en la ZONA DEL PAÍS DEL CASO (RN-82): `24/09/2026, 03:00 p. m.`.
 *
 * AQUÍ SÍ SE USA `Date`, Y ES LO CORRECTO. `comoFecha` lo evita porque una fecha sola no
 * tiene hora y `new Date('2026-09-03')` la situaría a medianoche UTC (el 2 de septiembre
 * en Lima). Un instante SÍ es un punto en el tiempo: lo que hay que decidir es en qué zona se
 * lee.
 *
 * EL DEFECTO QUE CIERRA (Bug #1087, 6-oct-2026)
 * ----------------------------------------------
 * Se leía en la zona del NAVEGADOR. Un caso de Chile visto desde Lima decía otra hora que en
 * Chile, y cerca de la medianoche otro día que el que `comoFechaDeInstante` muestra para el mismo
 * instante. Decisión del operador: la hora de un instante es la del país del caso, la misma zona
 * de la que sale su día. La zona es obligatoria a propósito, como en `comoFechaDeInstante`:
 * cada pantalla dice de qué país es el dato (`useZonaDelPais`) y olvidarlo no compila. `null`
 * (sin zona, o un dato que no es de ningún caso ni país) se lee en Lima.
 *
 * El formato no cambia: `dateStyle: 'short'` escribía «25/09/26», y con las opciones explícitas
 * el año va siempre completo.
 */
export function comoFechaYHora(valor: unknown, zonaHoraria: string | null | undefined): string {
  if (valor === null || valor === undefined || valor === '') return '';
  const instante = new Date(String(valor));
  if (Number.isNaN(instante.getTime())) return String(valor);
  return instante.toLocaleString('es-PE', {
    timeZone: zonaQueSeUsa(zonaHoraria),
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

/**
 * Un instante con su hora en 24 h y sin coma, en la ZONA DEL PAÍS DEL CASO (RN-82):
 * `07/10/2026 00:30`. Es el formato de la trazabilidad, el mismo que imprime su documento
 * exportado (`trazabilidad_impresa.py`): pantalla e impreso tienen que decir la misma hora.
 */
export function comoFechaYHora24(valor: unknown, zonaHoraria: string | null | undefined): string {
  if (valor === null || valor === undefined || valor === '') return '';
  const instante = new Date(String(valor));
  if (Number.isNaN(instante.getTime())) return String(valor);
  const p = paredEnZona(instante, zonaHoraria);
  const dos = (n: number) => String(n).padStart(2, '0');
  return `${dos(p.dia)}/${dos(p.mes)}/${p.anio} ${dos(p.hora)}:${dos(p.minuto)}`;
}

/**
 * Solo la hora de un instante (`03:00 p. m.`), en la ZONA DEL PAÍS DEL CASO (RN-82). La usa
 * «Guardado hh:mm» del registro: el autoguardado es un instante y su hora es la del país del caso.
 */
export function comoHora(instante: Date, zonaHoraria: string | null | undefined): string {
  return instante.toLocaleTimeString('es-PE', { timeZone: zonaQueSeUsa(zonaHoraria), hour: '2-digit', minute: '2-digit' });
}
